/**
 * Wizard step 1 — the dropzone, the file card and the transfer controls (M6 §5).
 *
 * The component owns no transfer state of its own: `state` is the wizard's reducer state
 * and every button calls back up to `UploadWizard`, which holds the `AbortController`. That
 * is what lets pause survive a re-render — the controller is a ref on the parent, not a
 * closure in here.
 */
import { useCallback, useRef, useState, type DragEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { Btn } from '../../components/Btn';
import type { ManagedDatasetRow } from '../../lib/api';
import { Icon } from '../../lib/icons';
import {
  ACCEPTED_EXTENSIONS,
  fmtBytes,
  isEmail,
  submitterOk,
  type MetadataForm,
  type WizardState,
} from './manager';

export interface DropzoneProps {
  state: WizardState;
  /**
   * The datasets this upload may version, for naming the Replace target in full. The step
   * chooses nothing itself any more (client, 2026-09-19): a new upload is a new dataset, and
   * "put new data behind a published one" is the Replace rail button's own first step.
   */
  datasets: ManagedDatasetRow[];
  /**
   * Replace mode (client, 2026-09-18): the target was settled in the step before this one,
   * so the select is a statement rather than a question.
   */
  onPick: (file: File) => void;
  /** The submitter block writes into the wizard's form like every other field. */
  onForm: (patch: Partial<MetadataForm>) => void;
  onPause: () => void;
  onResume: () => void;
  onCancel: () => void;
}

export function Dropzone({
  state,
  datasets,
  onPick,
  onForm,
  onPause,
  onResume,
  onCancel,
}: DropzoneProps) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);

  const onDrop = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setOver(false);
      const file = e.dataTransfer?.files?.[0];
      if (file) onPick(file);
    },
    [onPick],
  );

  /**
   * The server requires the submitter on `POST /uploads`, which is the *first* call a picked
   * file makes — so the block gates the dropzone rather than being validated at Next. A file
   * dropped before the two fields are answered would open a session the server refuses, and
   * the reader would read that refusal as "my file is wrong".
   */
  const who = submitterOk(state.form);
  const emailTyped = state.form.submitterEmail.trim() !== '';

  const pct = Math.round(state.progress.ratio * 100);
  const uploading = state.phase === 'uploading';
  const paused = state.phase === 'paused';
  const failed = state.phase === 'error';

  return (
    <div className="mgr-step" data-testid="mgr-step-1">
      <fieldset className="mgr-submitter" data-testid="mgr-submitter">
        <legend className="mgr-label">{t('manager.submitter.legend')}</legend>
        <div className="mgr-form">
          <label className="mgr-field" htmlFor="mgr-f-submitter_name">
            <span className="mgr-label">
              {t('manager.submitter.name')} <b aria-hidden="true">*</b>
            </span>
            <input
              id="mgr-f-submitter_name"
              data-testid="mgr-f-submitter_name"
              className="mgr-input"
              required
              autoComplete="name"
              value={state.form.submitterName}
              onChange={(e) => onForm({ submitterName: e.target.value })}
            />
          </label>
          <label className="mgr-field" htmlFor="mgr-f-submitter_email">
            <span className="mgr-label">
              {t('manager.submitter.email')} <b aria-hidden="true">*</b>
            </span>
            <input
              id="mgr-f-submitter_email"
              data-testid="mgr-f-submitter_email"
              className="mgr-input"
              type="email"
              required
              autoComplete="email"
              value={state.form.submitterEmail}
              onChange={(e) => onForm({ submitterEmail: e.target.value })}
            />
            <span className="mgr-hint" data-testid="mgr-submitter-hint">
              {emailTyped && !isEmail(state.form.submitterEmail)
                ? t('manager.submitter.badEmail')
                : t('manager.submitter.hint')}
            </span>
          </label>
        </div>
      </fieldset>

      {/* The target named in full — title *and* id. The id is what the API is given and what
          a reader checking a handoff from the Datasets tab needs to recognise; the title on
          its own would leave two similarly named datasets indistinguishable. */}
      {state.versionOf ? (
        <p className="mgr-versionOf" data-testid="mgr-wizard-versionOf">
          {t('manager.versionOf.target', {
            title: datasets.find((d) => d.id === state.versionOf)?.title ?? state.versionOf,
          })}{' '}
          <code>{state.versionOf}</code>
        </p>
      ) : null}

      <div
        className={`mgr-drop${over ? ' over' : ''}${who ? '' : ' off'}`}
        data-testid="mgr-drop"
        onDragOver={(e) => {
          e.preventDefault();
          setOver(true);
        }}
        onDragLeave={() => setOver(false)}
        onDrop={who ? onDrop : (e) => e.preventDefault()}
      >
        <Icon name="upload" size={28} />
        <p className="mgr-drop-title">{t('manager.drop.title')}</p>
        <Btn variant="primary" disabled={!who} onClick={() => inputRef.current?.click()}>
          {t('manager.drop.browse')}
        </Btn>
        {who ? null : (
          <p className="mgr-drop-cap" data-testid="mgr-drop-needs-submitter">
            {t('manager.submitter.needed')}
          </p>
        )}
        <p className="mgr-drop-formats">{t('manager.drop.formats')}</p>
        <p className="mgr-drop-cap">{t('manager.drop.cap')}</p>
        <input
          ref={inputRef}
          type="file"
          className="mgr-file-input"
          data-testid="mgr-file-input"
          aria-label={t('manager.drop.inputLabel')}
          accept={ACCEPTED_EXTENSIONS.join(',')}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) onPick(file);
            // Clearing lets the same file be picked again after a cancel.
            e.target.value = '';
          }}
        />
      </div>

      {state.error && !state.file ? (
        <p className="mgr-error" role="alert" data-testid="mgr-drop-error">
          {t(`manager.err.${state.error}`)}
        </p>
      ) : null}

      {state.file ? (
        <div className="mgr-filecard" data-testid="mgr-file">
          <div className="mgr-filecard-head">
            <Icon name="db" size={18} />
            <div className="mgr-filecard-text">
              <strong>{state.file.name}</strong>
              <span>
                {fmtBytes(state.file.size)}
                {state.row?.format ? ` · ${state.row.format}` : ''}
              </span>
            </div>
            <div className="mgr-filecard-actions">
              {uploading ? (
                <Btn size="sm" icon="pause" onClick={onPause}>
                  {t('manager.file.pause')}
                </Btn>
              ) : null}
              {paused || failed ? (
                <Btn size="sm" icon="play" onClick={onResume}>
                  {failed ? t('manager.file.retry') : t('manager.file.resume')}
                </Btn>
              ) : null}
              <Btn size="sm" icon="x" onClick={onCancel}>
                {t('manager.file.cancel')}
              </Btn>
            </div>
          </div>
          <div
            className="mgr-progress"
            data-testid="mgr-progress"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={pct}
            aria-label={state.file.name}
          >
            <span className="mgr-progress-bar" style={{ width: `${pct}%` }} />
          </div>
          <p className="mgr-progress-note">
            {state.phase === 'ready'
              ? t('manager.file.done')
              : paused
                ? t('manager.file.paused', { pct })
                : failed
                  ? t('manager.err.upload')
                  : t('manager.file.progress', {
                      pct,
                      sent: fmtBytes(state.progress.sent),
                      total: fmtBytes(state.progress.total),
                    })}
          </p>
        </div>
      ) : null}

    </div>
  );
}
